import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCircleExclamation, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { useRolePermissions } from '@rfacil/shared/hooks';
import { useAuthStore, usePermissionsStore } from '@rfacil/shared/services';
import { Badge, CustomButton, Modal } from '@rfacil/shared/ui';
import { useContadorBuzon, useResumenBuzon } from '../hooks/useBuzon';
import { RUTA_BUZON, estadoGloboBuzon, normalizarAccionablesBuzon } from '../features/buzon/buzonPresentacion';
import { CLASES_TONO_BUZON, categoriaVisualBuzon } from '../features/buzon/categoriasBuzon';
import { saludoBuzon } from '../features/buzon/inteligenciaBuzon';
import {
  avisoBuzonYaMostrado,
  debeMostrarAvisoBuzon,
  llaveSesionAvisoBuzon,
  marcarAvisoBuzonMostrado,
} from '../features/buzon/avisoInicioSesion';

/** Categorías que caben en el aviso; las demás se resumen en "y N categorías más". */
const MAXIMO_CATEGORIAS_AVISO = 3;

const CLASES_BOTON_BASE = 'inline-flex min-h-11 items-center justify-center px-4 text-sm font-semibold';

const CLASES_BOTON_SECUNDARIO = `${CLASES_BOTON_BASE} border border-gray-200 bg-background-primary text-host-500 hover:bg-host-50`;

const CLASES_BOTON_PRINCIPAL = `${CLASES_BOTON_BASE} border border-host-500 bg-host-500 text-white hover:bg-host-600`;

/**
 * @component AvisoPendientesBuzon
 * @description Aviso al iniciar sesión cuando hay pendientes en el buzón (M3-H1). Se monta una vez en
 * `AppContent`, fuera de las rutas, y se muestra una sola vez por sesión: cuando ya cargaron los
 * permisos, el rol tiene buzón, hay pendientes y no está abierto el cambio de contraseña. Comparte la
 * caché del contador con la campanita y no lo sondea: solo lo pide al montarse (con `staleTime: 0`) y
 * al volver a la pestaña; el sondeo de cada minuto es de la campanita. El resumen por categoría solo
 * se pide con el aviso abierto.
 *
 * Arriba va una franja con el degradado del sistema (campana, saludo, título y chips de contexto); abajo
 * el desglose por categoría, a lo más 3 filas.
 */
export const AvisoPendientesBuzon: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { canViewBuzon } = useRolePermissions();
  const autenticado = useAuthStore((s) => s.isAuthenticated);
  const idUsuario = useAuthStore((s) => Number(s.idUser) || 0);
  const expiracion = useAuthStore((s) => s.expiracion);
  const nombreCompleto = useAuthStore((s) => s.user?.nombreCompleto);
  const cambioDeContrasenaAbierto = useAuthStore((s) => !!s.pendingPasswordChange || !!s.showVoluntaryPasswordChange);
  const permisosCargando = usePermissionsStore((s) => s.isLoading);

  const llave = useMemo(() => llaveSesionAvisoBuzon(idUsuario, expiracion), [idUsuario, expiracion]);
  const [abierto, setAbierto] = useState(false);

  const tieneBuzon = autenticado && canViewBuzon && idUsuario > 0;
  const { data: contador, isError: contadorConError } = useContadorBuzon(idUsuario, tieneBuzon);
  const { data: resumen } = useResumenBuzon(idUsuario, abierto);
  const pendientes = contador?.Pendientes ?? 0;

  useEffect(() => {
    if (
      !abierto &&
      debeMostrarAvisoBuzon({
        autenticado,
        tieneBuzon,
        permisosCargando,
        cambioDeContrasenaAbierto,
        pendientes,
        rutaActual: pathname,
        yaMostrado: avisoBuzonYaMostrado(llave),
      })
    ) {
      // La marca se deja al mostrarlo: recargar la página no vuelve a sacar el aviso.
      marcarAvisoBuzonMostrado(llave);
      setAbierto(true);
    }
  }, [abierto, autenticado, tieneBuzon, permisosCargando, cambioDeContrasenaAbierto, pendientes, pathname, llave]);

  // Si la sesión termina con el aviso abierto, se cierra.
  useEffect(() => {
    if (!autenticado) {
      setAbierto(false);
    }
  }, [autenticado]);

  const categorias = (resumen?.Categorias ?? []).filter((c) => c.Pendientes > 0);
  const visibles = categorias.slice(0, MAXIMO_CATEGORIAS_AVISO);
  const restantes = categorias.length - visibles.length;
  const noLeidas = contador?.NoLeidas ?? 0;
  // `Accionables` es un campo nuevo del SSO: sin él, o si la última petición falló (lo que quedó en
  // caché puede ser de otro momento), no se dice cuántas requieren acción.
  const accionables = normalizarAccionablesBuzon(contadorConError ? undefined : contador?.Accionables);
  // Mismo globo que la campanita de la barra: las sin leer y, si todo está leído, el punto de tareas.
  const globo = estadoGloboBuzon({ NoLeidas: noLeidas, Accionables: accionables });

  return (
    <Modal isOpen={abierto} onClose={() => setAbierto(false)} size="sm" bare>
      {/* El Modal compartido no se anuncia como diálogo: se marca aquí para lectores de pantalla. */}
      <div role="dialog" aria-modal="true" aria-labelledby="aviso-buzon-titulo">
        <div className="relative overflow-hidden bg-gradient-to-br from-host-500 to-host-300 px-6 pt-6 pb-5 text-text-secondary">
          {/* Círculos decorativos, como en PageHeader. */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-16 h-44 w-44 rounded-full bg-white opacity-5" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 -left-10 h-32 w-32 rounded-full bg-white opacity-5" />

          <div className="relative">
            <div
              aria-hidden="true"
              className="relative flex h-13 w-13 items-center justify-center rounded-2xl bg-white shadow-lg"
            >
              <FontAwesomeIcon icon={faBell} className="origin-top text-[22px] text-host-500 motion-safe:animate-campanita" />
              {globo.tipo === 'numero' && (
                <span
                  data-globo="numero"
                  className="absolute -top-1.5 -right-2 flex h-[22px] min-w-[22px] items-center justify-center rounded-full border-2 border-host-500 bg-materialidad-rojo-600 px-1.5 text-[11px] font-bold leading-none text-white"
                >
                  {globo.texto}
                </span>
              )}
              {globo.tipo === 'punto' && (
                <span
                  data-globo="punto"
                  className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full border-2 border-host-500 bg-warning-200"
                />
              )}
            </div>

            <p className="mt-4 text-sm text-host-50">{saludoBuzon(new Date(), nombreCompleto)}</p>
            <h2 id="aviso-buzon-titulo" className="mt-0.5 text-xl font-bold leading-tight text-text-secondary">
              Tienes {pendientes} {pendientes === 1 ? 'pendiente' : 'pendientes'} en tu buzón
            </h2>

            {(accionables > 0 || noLeidas > 0) && (
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {accionables > 0 && (
                  <Badge variant="warning" size="sm" solid className="gap-1.5 font-semibold">
                    <FontAwesomeIcon icon={faCircleExclamation} className="text-[11px]" />
                    {accionables} {accionables === 1 ? 'requiere' : 'requieren'} tu acción
                  </Badge>
                )}
                {noLeidas > 0 && (
                  <Badge size="sm" className="gap-1.5 bg-white/15 font-semibold text-white">
                    <FontAwesomeIcon icon={faEnvelope} className="text-[11px]" />
                    {noLeidas} sin leer
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>

        {visibles.length > 0 && (
          <div className="px-5 pt-4 pb-1.5">
            <p id="aviso-buzon-categorias" className="mb-2 text-xs font-semibold text-gray-500">
              Por categoría
            </p>
            <ul aria-labelledby="aviso-buzon-categorias">
              {visibles.map((c) => {
                const { icono, tono } = categoriaVisualBuzon(c.Clave);
                const clasesTono = CLASES_TONO_BUZON[tono];
                return (
                  <li
                    key={c.Clave}
                    className="grid grid-cols-[34px_minmax(0,1fr)_auto] items-center gap-3 border-b border-gray-100 px-1 py-2.5 last:border-b-0"
                  >
                    <span
                      aria-hidden="true"
                      className={`flex h-[34px] w-[34px] items-center justify-center rounded-[10px] ${clasesTono.fondo} ${clasesTono.icono}`}
                    >
                      <FontAwesomeIcon icon={icono} className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-text-primary">{c.Nombre}</p>
                      <p className="text-xs text-gray-500">{c.NoLeidas > 0 ? `${c.NoLeidas} sin leer` : 'Todas leídas'}</p>
                    </div>
                    <Badge size="sm" className="min-w-[26px] justify-center bg-host-500 font-semibold text-white">
                      {c.Pendientes}
                      <span className="sr-only"> {c.Pendientes === 1 ? 'pendiente' : 'pendientes'}</span>
                    </Badge>
                  </li>
                );
              })}
            </ul>
            {restantes > 0 && (
              <p className="px-1 pt-2 pb-0.5 text-xs text-gray-500">
                y {restantes} {restantes === 1 ? 'categoría más' : 'categorías más'}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2.5 px-5 pt-4 pb-5">
          <CustomButton type="button" text="Más tarde" onClick={() => setAbierto(false)} className={CLASES_BOTON_SECUNDARIO} />
          <CustomButton
            type="button"
            text="Ver mi buzón"
            onClick={() => {
              setAbierto(false);
              navigate(RUTA_BUZON);
            }}
            className={CLASES_BOTON_PRINCIPAL}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AvisoPendientesBuzon;
